import React, { useState, useEffect } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { db } from '../../db/shiksha-db';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';

export default function DPDPConsentPage() {
  const [fiduciaryChecked, setFiduciaryChecked] = useState(false);
  const [processingChecked, setProcessingChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  // The page the admin was originally trying to reach before being redirected here
  const returnTo = location.state?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    // Check if they already consented locally
    const checkLocalConsent = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const existingConsent = await db.dpdpConsent
        .where('adminEmail')
        .equals(user.email)
        .first();
      
      if (existingConsent && existingConsent.status === 'agreed') {
        navigate(returnTo, { replace: true });
      }
    };
    checkLocalConsent();
  }, [auth, navigate]);

  const handleSubmit = async () => {
    if (!fiduciaryChecked || !processingChecked) {
      setError('You must agree to all policies to proceed.');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // 1. Send consent to backend for official audit trail
      const token = await user.getIdToken();
      const apiUrl = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
      
      const response = await fetch(`${apiUrl}/api/admin/consent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fiduciaryAgreed: fiduciaryChecked,
          processingAgreed: processingChecked,
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to record consent on server. Are you online?');
      }

      // 2. Save locally so we don't ask again on this device
      await db.dpdpConsent.add({
        adminEmail: user.email,
        timestamp: Date.now(),
        status: 'agreed'
      });
      
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err.message || 'An error occurred while saving your consent.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        
        <div className="flex items-center mb-6">
          <div className="bg-indigo-100 p-3 rounded-full mr-4">
            <ShieldCheck className="w-8 h-8 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Data Privacy & Security Agreement
            </h1>
            <p className="text-sm text-gray-500">
              Mandatory compliance for the Digital Personal Data Protection (DPDP) Act, 2023
            </p>
          </div>
        </div>

        <div className="prose prose-sm text-gray-600 mb-8">
          <p>
            Under the Government of India's DPDP Act 2023, this institution acts as a <strong>Data Fiduciary</strong> for student information. 
            Because we process data of minors, we are subject to strict regulatory requirements regarding data minimization, purpose limitation, and audit trails.
          </p>
          <p>
            Before accessing the administrative dashboard, creating teacher accounts, or uploading student data, you must formally acknowledge your responsibilities.
            <br />
            <em>Note: This action is recorded with your IP Address, location, and timestamp for official audit purposes.</em>
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="space-y-8 mb-8">
          {/* Checkbox 1 */}
          <label className="flex items-start cursor-pointer group">
            <div className="flex items-center h-8">
              <input
                type="checkbox"
                className="w-8 h-8 text-indigo-600 bg-gray-100 border-gray-400 rounded focus:ring-indigo-500 cursor-pointer"
                checked={fiduciaryChecked}
                onChange={(e) => setFiduciaryChecked(e.target.checked)}
                disabled={isSubmitting}
              />
            </div>
            <div className="ml-5">
              <span className="block text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                I confirm I am an authorized representative of this institution.
              </span>
              <p className="mt-2 text-base text-gray-700 leading-relaxed">
                I have the legal authority to act as the Data Fiduciary and manage student records on behalf of the school.
              </p>
            </div>
          </label>

          {/* Checkbox 2 */}
          <label className="flex items-start cursor-pointer group">
            <div className="flex items-center h-8">
              <input
                type="checkbox"
                className="w-8 h-8 text-indigo-600 bg-gray-100 border-gray-400 rounded focus:ring-indigo-500 cursor-pointer"
                checked={processingChecked}
                onChange={(e) => setProcessingChecked(e.target.checked)}
                disabled={isSubmitting}
              />
            </div>
            <div className="ml-5">
              <span className="block text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                I agree to the DPDP Data Processing Guidelines.
              </span>
              <p className="mt-2 text-base text-gray-700 leading-relaxed">
                I will ensure student demographic and academic data is collected strictly for educational purposes, will not be used for profiling/tracking, and that verifiable parental consent has been obtained.
              </p>
            </div>
          </label>
        </div>

        <div className="pt-6 border-t border-gray-200">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !fiduciaryChecked || !processingChecked}
            className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 ${
              isSubmitting || !fiduciaryChecked || !processingChecked
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm'
            }`}
          >
            {isSubmitting ? 'Recording Audit Trail...' : 'I Agree & Proceed to Dashboard'}
          </button>
        </div>
        
      </div>
    </div>
  );
}
